import { useState } from "react";
import axios from "axios";
import { MapPin, Mail, Phone, Send, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    country: "",
    product_interest: "",
    quantity: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-cream">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-forest mb-6">
              Request a <span className="text-sand">Quote</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Ready to partner with a reliable manufacturer? Fill out the form
              below and our team will get back to you within 24 hours with a
              customized quote.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-forest mb-6">
                  Get in Touch
                </h2>
                <p className="text-muted-foreground">
                  Have questions? We're here to help. Reach out to us through any
                  of these channels.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">USA Address</h3>
                    <p className="text-muted-foreground text-sm">
                      Reliable Exports
                      <br />
                      10430 Montgomery Ave
                      <br />
                      Granada Hills, CA 91344, USA
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">India Offices</h3>
                    <p className="text-muted-foreground text-sm">
                      <span className="font-semibold text-forest">Registered office -</span>
                      <br />
                      5, Gujrawala Town - II
                      <br />
                      Delhi - 110009, India
                      <br />
                      <br />
                      <span className="font-semibold text-forest">Manufacturing -</span>
                      <br />
                      C 106, Mayapuri Industrial Area, Phase - 2
                      <br />
                      Delhi - 110064, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">Phone</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      <a
                        href="tel:+12134712170"
                        className="hover:text-sand transition-colors"
                      >
                        USA TEL : 213 - 471 - 2170
                      </a>
                      <br />
                      <a
                        href="tel:+911141448168"
                        className="hover:text-sand transition-colors"
                      >
                        India Tel : +91 11 - 41448168
                      </a>
                      <br />
                      <span>
                        Cell -{" "}
                        <a
                          href="tel:+919910299995"
                          className="hover:text-sand transition-colors"
                        >
                          +91 9910299995
                        </a>
                        {", "}
                        <a
                          href="tel:+919810272098"
                          className="hover:text-sand transition-colors"
                        >
                          9810272098
                        </a>
                      </span>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-forest mb-1">Email</h3>
                    <a
                      href="mailto:amit@reliableexports.in"
                      className="text-muted-foreground text-sm hover:text-sand"
                    >
                      amit@reliableexports.in
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {isSuccess ? (
                <div
                  data-testid="contact-success"
                  className="bg-white rounded-2xl p-8 md:p-12 border border-sand/20 shadow-card text-center"
                >
                  <div className="w-20 h-20 bg-forest/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-forest" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-forest mb-4">
                    Thank You!
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Your inquiry has been submitted successfully. Our team will
                    review your requirements and get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => {
                      setIsSuccess(false);
                      setFormData({
                        name: "",
                        email: "",
                        company: "",
                        phone: "",
                        country: "",
                        product_interest: "",
                        quantity: "",
                        message: "",
                      });
                    }}
                    className="bg-forest hover:bg-forest-dark text-white rounded-full px-8"
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="bg-white rounded-2xl p-8 md:p-10 border border-sand/20 shadow-card"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-forest">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="bg-cream border-sand/30 focus:border-sand"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-forest">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        className="bg-cream border-sand/30 focus:border-sand"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-forest">
                        Company Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="contact-company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        className="bg-cream border-sand/30 focus:border-sand"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-forest">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="contact-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 890"
                        className="bg-cream border-sand/30 focus:border-sand"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-forest">
                        Country
                      </Label>
                      <Select
                        value={formData.country}
                        onValueChange={(value) =>
                          handleSelectChange("country", value)
                        }
                      >
                        <SelectTrigger
                          data-testid="contact-country"
                          className="bg-cream border-sand/30"
                        >
                          <SelectValue placeholder="Select Country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="usa">United States</SelectItem>
                          <SelectItem value="uk">United Kingdom</SelectItem>
                          <SelectItem value="germany">Germany</SelectItem>
                          <SelectItem value="france">France</SelectItem>
                          <SelectItem value="australia">Australia</SelectItem>
                          <SelectItem value="canada">Canada</SelectItem>
                          <SelectItem value="uae">UAE</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product_interest" className="text-forest">
                        Product Interest
                      </Label>
                      <Select
                        value={formData.product_interest}
                        onValueChange={(value) =>
                          handleSelectChange("product_interest", value)
                        }
                      >
                        <SelectTrigger
                          data-testid="contact-product"
                          className="bg-cream border-sand/30"
                        >
                          <SelectValue placeholder="Select Product" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="jute_shopping">
                            Jute Shopping Bags
                          </SelectItem>
                          <SelectItem value="cotton_tote">
                            Cotton Tote Bags
                          </SelectItem>
                          <SelectItem value="wine_bags">Wine Bags</SelectItem>
                          <SelectItem value="promotional">
                            Promotional Bags
                          </SelectItem>
                          <SelectItem value="custom">Custom Products</SelectItem>
                          <SelectItem value="multiple">Multiple Products</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="quantity" className="text-forest">
                        Estimated Quantity
                      </Label>
                      <Select
                        value={formData.quantity}
                        onValueChange={(value) =>
                          handleSelectChange("quantity", value)
                        }
                      >
                        <SelectTrigger
                          data-testid="contact-quantity"
                          className="bg-cream border-sand/30"
                        >
                          <SelectValue placeholder="Select Quantity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1000-5000">1,000 - 5,000</SelectItem>
                          <SelectItem value="5000-10000">
                            5,000 - 10,000
                          </SelectItem>
                          <SelectItem value="10000-50000">
                            10,000 - 50,000
                          </SelectItem>
                          <SelectItem value="50000-100000">
                            50,000 - 100,000
                          </SelectItem>
                          <SelectItem value="100000+">100,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="message" className="text-forest">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        data-testid="contact-message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your requirements, including sizes, colors, printing needs, and any specific details..."
                        className="bg-cream border-sand/30 focus:border-sand min-h-[150px]"
                      />
                    </div>
                  </div>

                  {error && (
                    <p
                      data-testid="contact-error"
                      className="text-red-500 text-sm mt-4"
                    >
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={isSubmitting}
                    className="w-full mt-6 bg-sand hover:bg-sand-dark text-white font-medium rounded-full py-6 transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Inquiry
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
